import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import Factories from "@/utils/factories.ts";
// import DashboardClass from "@/utils/dashboard_class.ts";
import {useAtom} from "jotai/index";
import {user_credits} from "@/atoms/profile.ts";
import {CreditTypes, LoanTypes} from "@/types_and_interfaces/types.ts";
import LoanBodyInterface, {UncommitedInterface} from "@/types_and_interfaces/loan_body_interface.ts";
import {loansReqs} from "@/services/loans.ts";
import CommonClass from "@/utils/common.ts";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import * as Sentry from "@sentry/react";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import {useTranslation} from "react-i18next";
import {contract, contract_pg_nr, cr_line_contract, pending_loans} from "@/atoms/common.ts";
import {isMobile} from "react-device-detect";
import {getDocument} from "pdfjs-dist";
import CrLineTransactionInterface from "@/types_and_interfaces/cr_line_transaction_interface.ts";
import {country_code, spare_phone_number} from "@/utils/constants.ts";
import UserInterface from "@/types_and_interfaces/user_interface.ts";
import test_pending_loans from "@/assets/test.json"
import CMS from "@/utils/cms.ts";

function useLoans(){
    const [userCredits, setUserCredits] = useAtom(user_credits)
    const [pend_loans, setPendingLoans] = useAtom(pending_loans)
    const [, set_contract_atom] = useAtom(contract)
    const [, set_cr_line_contract_atom] = useAtom(cr_line_contract)
    const {t} = useTranslation()
    const [, set_contract_nr ] = useAtom(contract_pg_nr)
    // @ts-ignore
    const pend_status_loans_length = [...(pend_loans?.pending_submission || []),
        // @ts-ignore
        ...(pend_loans?.pending_approval || []),
        // @ts-ignore
        ...(pend_loans?.submitting || []),
        // @ts-ignore
        ...(pend_loans?.approved || []),
        // @ts-ignore
        ...(pend_loans?.approved_and_payment_pending || []),
        // @ts-ignore
        ...(pend_loans?.approved_and_received || []),
        // @ts-ignore
        ...(pend_loans?.approved_and_checked || []),
        // @ts-ignore
        ...(pend_loans?.approved_and_not_verified || []),
        // @ts-ignore
        ...(pend_loans?.approved_and_need_sign || []),
        // @ts-ignore
        ...(pend_loans?.transmission_error || []),
    ].length || 0
    
    const { executeRecaptcha } = useGoogleReCaptcha();
    
    const getRecaptchaToken = async () => {
        try {
            if(CommonClass.general_settings.recaptcha.is_enabled && executeRecaptcha) {
                return await executeRecaptcha('homepage');
            } else {
                return undefined
            }

        } catch (e) {
            Sentry.captureException(e, { tags: { section: "recaptcha", action: "get_token_loans" } });
            return undefined
        }
    }
    
    const clearLoanApplications = () => {
        setPendingLoans(undefined)
    }
    
    const clearCredits = () => {
        setUserCredits(undefined)
    }
    
    const apply_for_loan = async (data: LoanBodyInterface) => {
        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        const req = await loansReqs().apply_loan(data, r_token)
        
        if(req?.status === 200 && req?.response.hasOwnProperty("message") && req.response.message === "ok"){
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "success", t("send_cv_confirm"))
            
            return true
            
        } else {

            eventEmitter.emit(
                CommonClass.CustomEvents.update_snackbar,
                "error", t(req.message as string).replace("{{number}}", CMS?.general_info?.phone_number || spare_phone_number)
            )
            
            return false
        }
    
    }

    const updateUserLoans = async () => {
        
        const test = false
        
        if(test) {
            let loans:Array<LoanInterface> = test_pending_loans.map((obj:any) => {
                return Factories.loan_factory(obj)
            })
            
            let timeout = setTimeout(() => {
                setUserCredits(loans)
                clearTimeout(timeout)
            }, 1000)
            
            return
        }

        let res1:Array<LoanInterface> = []
        let res2:Array<LoanInterface> = []
        
        let req = await loansReqs().fetchLoanList()

        if(!req.error && req?.response.hasOwnProperty("data")) {
            if (Array.isArray(req?.response.data)) {

                let loans:Array<LoanInterface> = req?.response.data.map((obj:any) => {
                    return Factories.loan_factory(obj)
                })
                
                loans = loans.sort((a, b) => {
                    const dateA = a.origin_created_at ? new Date(a.origin_created_at) : new Date(0);
                    const dateB = b.origin_created_at ? new Date(b.origin_created_at) : new Date(0);
                    return dateB.getTime() - dateA.getTime();
                })
                
                res1 = loans

            } else {
                res1 = []
            }

        }
        
        req = await loansReqs().fetch_cr_line_loan_list()
        
        if(!req.error && req?.response.hasOwnProperty("data")) {
            if (Array.isArray(req?.response.data)) {

                let loans:Array<LoanInterface> = req?.response.data.map((obj:any) => {
                    return Factories.loan_factory(obj)
                })

                loans = loans.sort((a, b) => {
                    const dateA = a.origin_created_at ? new Date(a.origin_created_at) : new Date(0);
                    const dateB = b.origin_created_at ? new Date(b.origin_created_at) : new Date(0);
                    return dateB.getTime() - dateA.getTime();
                })
                res2 = loans
                // setUserCredits(loans)

            } else {
                res2 = []
                // setUserCredits([])
            }

        }
        setUserCredits([...res1, ...res2])
    }

    const getLoansByType = (x: LoanTypes):Array<LoanInterface> | undefined => {
        if(Array.isArray(userCredits)) {
            return userCredits.filter((obj:LoanInterface) => obj.product_group === x)
        }

        return undefined
    }

    const getStandardCredits = ():Array<LoanInterface> | undefined => {
        if(Array.isArray(userCredits)) {
            return userCredits.filter((obj:LoanInterface) => obj.product_group !== "Credit Line")
        }
        
        return undefined
    }
    
    const get_last_cr_line = ():LoanInterface | undefined => {
        let res = undefined
        const cr_line_loans = getLoansByType("Credit Line")?.filter((x:any) => x.r_status === "approved")
        if(Array.isArray(cr_line_loans) && cr_line_loans.length) {
            let latestObject = cr_line_loans[0]
            for (let i = 1; i < cr_line_loans.length; i++) {
                const current = cr_line_loans[i];
                const latestDate = new Date(latestObject.origin_created_at);
                const currentDate = new Date(current.origin_created_at);

                if (currentDate > latestDate) {
                    latestObject = current;
                }
            }
            res = latestObject
        }

        return res
    }

    const getExpiredLoans = ():Array<LoanInterface> => {
        if(!Array.isArray(userCredits)) return []
        if(!userCredits.length) return []

        return userCredits.filter((x) => x.r_status === "settled")
    }

    const getLoanByContract = (id: string):LoanInterface | undefined => {
        if(Array.isArray(userCredits) && userCredits.length) {
            return userCredits.find((x:LoanInterface) => x.urn === id)
        }
        return undefined
    }
    
    const getCrLineTransaction = (id:string):CrLineTransactionInterface | undefined => {
        
        let res:CrLineTransactionInterface | undefined = undefined
        
        if(Array.isArray(userCredits) && userCredits.length) {
            let cr =  userCredits.find((x:LoanInterface) => x.product_group === 'Credit Line')
            
            if(typeof cr !== undefined) {
                if( Array.isArray(cr?.transactions) && cr?.transactions.length ) {
                    res = cr?.transactions.find((x:CrLineTransactionInterface) => x.urn_tr === id) || undefined
                }
            }
        }
        
        return res
    }
    
    const updPendingLoans = async () => {
        const req = await loansReqs().pending_loans()
        
        if(req?.status === 200 && req?.response.hasOwnProperty("message") && req.response.message === "ok"){

            const raw_data = req.response.data

            if(typeof raw_data === "object" && Object.keys(raw_data)){
                setPendingLoans(Factories.loan_application_types_factory(raw_data))
                
            } else {
                setPendingLoans({})
                
            }
            
        } else setPendingLoans({})
    }
    
    const getContract = async (data:any, urn:string, local?:boolean) => {
        
        const req = await loansReqs().contract(data);

        let local_contr:any = undefined

        if (req.status === 200 && !req?.error) {
            const raw_data = req.response;

            if (raw_data instanceof Blob) {
                const blobUrl = URL.createObjectURL(raw_data);

                if(local) local_contr = blobUrl

                set_contract_atom(prev => ({...prev, ...{[urn]:{ value: blobUrl }}}));
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_err"));
            }

        } else {
            if(!local) eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_err"));
            // eventEmitter.emit(CommonClass.ModalEvents.OpenGetContractModal, undefined)
        }

        if(local) return local_contr
    };
    
    const getTransactionContract = async (data:any, urn_tr:string) => {
        
        const req = await loansReqs().transaction_contract(data);
        
        if (req.status === 200 && !req?.error) {
            const raw_data = req.response;
            
            if (raw_data instanceof Blob) {
                const blobUrl = URL.createObjectURL(raw_data);

                set_cr_line_contract_atom(prev => ({...prev, ...{[urn_tr]:{ value: blobUrl }}}));
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_err"));
            }
            
        } else {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_err"));
            eventEmitter.emit(CommonClass.ModalEvents.OpenGetContractModal, undefined)
        }
    };
    
    const readContract = async (data:any) => {
        
        const req = await loansReqs().read_contract(data);
        
        if (req.status === 200 && !req?.error) {
        
        } else {
            
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_err"));
        }
    };
    
    const readTransactionContract = async (data:any) => {
        
        const req = await loansReqs().read_transaction_contract(data);
        
        if (req.status === 200 && !req?.error) {
        
        } else {
            
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_err"));
        }
    };
    
    const sendContract = async (data:any) => {
        
        let enable = true
        
        
        if(!data.email.length) {
            enable = false
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("type_email"));
            return enable
        }
        
        if(!CommonClass.isEmailValid(data.email)) {
            enable = false
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"));
            return enable
        }
        
        if(enable) {
            const req = await loansReqs().send_contract(data);
            
            if (req.status === 200 && !req?.error) {
                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "success", t("contract_sent_success"));
                return true
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("contract_sent_error"));
                return false
            }
        }
        
        return enable
    }
    
    const signContractSt1 = async (data:any) => {
        
        const req = await loansReqs().sign1(data)
        
        
        if(req?.status === 200){
            
            return true
        } else {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"));
            return false
        }
    }
    
    const signTransactionContractSt1 = async (data:any) => {
        
        const req = await loansReqs().sign_transaction_contract1(data)
        
        
        if(req?.status === 200){
            
            return true
        } else {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"));
            return false
        }
    }
    
    const signContractSt2 = async (data:any) => {
        
        const req = await loansReqs().sign2(data)
        
        
        if(req?.status === 200){
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, "");
            return true
            
        } else {
            
            if(req?.status === 400) {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("otp_err"));
                eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
                
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("error_message"));
                eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
                
            }

            return false
            
        }
    }
    
    const signTransactionContractSt2 = async (data:any) => {
        
        const req = await loansReqs().sign_transaction_contract2(data)
        
        
        if(req?.status === 200){
            eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, "");
            return true
        } else {
            if(req?.status === 400) {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("otp_err"));
                eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
                
            } else {
                eventEmitter.emit(CommonClass.CustomEvents.set_pin_err_text, t("error_message"));
                eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            }
            return false
        }
    }

    function convertDateFormat(dateString:any) {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }
    
    const get_schedule_html = async (productID:any, amount:any, months:any, fixed_expenses:any) => {
        
        eventEmitter.emit(CommonClass.CustomEvents.loading_schedule, true)

        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        const req = await loansReqs().get_payment_schedule(productID, amount, months, r_token)
        let html = ""
        
        if(req.status === 200 && req.response.data) {
            
            const schedule = req.response.data.PaymentsSchedule.Installment
            
            if(Array.isArray(schedule) && schedule.length) {
                
                let rows = schedule.sort((a: any, b: any) => {
                    return Number(a.aa) - Number(b.aa);
                }).map((item: any) => `
                    <tr>
                    
                        <td>${item.aa}</td>
                        <td>${convertDateFormat(item.InstallmentDate) || 0}</td>
                        <td>${item.InstallmentAmount || 0} ${t("currency")}</td>
                        <td>${item.InterestAmount || 0} ${t("currency")}</td>
                        <td>${item.CommissionAmount || 0} ${t("currency")}</td>
                        <td>${fixed_expenses} ${t("currency")}</td>
                        <td>${item.CapitalAmount || 0} ${t("currency")}</td>
                        <td>${item.RestCapitalAmount || 0} ${t("currency")}</td>

                    </tr>
                `).join('');
                
                html = `
                    <div class="schedule_table_container">
                        <table class="schedule_table">

                            <thead>
                                <tr>
                                    <th>${t("nr")}</th>
                                    <th>${t("date")}</th>
                                    <th>${t("sum")}</th>
                                    <th>${t("actual_interest")}</th>
                                    <th>${t("admin_commission")}</th>
                                    <th>${t("account_admin_commission")}</th>
                                    <th>${t("paid_capital")}</th>
                                    <th>${t("unpaid_capital")}</th>
                                </tr>
                            </thead>
    
                            <tbody>
                                ${rows}
                            </tbody>
    
                        </table>
                    </div>

                `
            }
        }
        
        eventEmitter.emit(CommonClass.CustomEvents.loading_schedule, false)
        
        return html
    }
    
    const prefetch_contract = async (data:any, urn:string) => {
        const req = await loansReqs().contract(data);

        if (req.status === 200 && !req?.error) {
            const raw_data = req.response;

            if (raw_data instanceof Blob) {
                const blobUrl = URL.createObjectURL(raw_data);

                set_contract_atom(prev => ({...prev, ...{[urn]:{ value: blobUrl }}}));
                if(isMobile) {
                    if (typeof blobUrl === 'string' && blobUrl.length) {
                        try {
                            const loadingTask = getDocument(blobUrl);
                            const pdf = await loadingTask.promise;
                            set_contract_nr(pdf._pdfInfo.numPages);
                            
                        } catch (err) {
                            Sentry.captureException(err, { tags: { section: "contract", action: "pdf_loading" } });
                        }
                    }
                }
            }
            
            return true
        } else {
            return false
            
        }
    }
    
    const prefetch_transaction_contract = async (data:any, urn_tr:string) => {
        
        const req = await loansReqs().transaction_contract(data);
        
        if (req.status === 200 && !req?.error) {
            const raw_data = req.response;
            
            if (raw_data instanceof Blob) {
                const blobUrl = URL.createObjectURL(raw_data);
                set_cr_line_contract_atom(prev => ({...prev, ...{[urn_tr]:{ value: blobUrl }}}));

                if(isMobile) {
                    if (typeof blobUrl === 'string' && blobUrl.length) {
                        try {
                            const loadingTask = getDocument(blobUrl);
                            const pdf = await loadingTask.promise;
                            set_contract_nr(pdf._pdfInfo.numPages);
                            
                        } catch (err) {
                            Sentry.captureException(err, { tags: { section: "contract", action: "pdf_loading" } });
                        }
                    }
                }
            }
            
            return true
            
        } else {
            return false
            
        }
    }

    const getMethods = async() => {
        
        if(typeof CommonClass.payment_methods_obj === "undefined") {

            let r_token:string | undefined  = undefined
            if(CommonClass.general_settings.recaptcha.is_enabled) {
                r_token = await getRecaptchaToken()
            }
            
            const req = await loansReqs().get_methods(r_token);
            
            if (req.status === 200 && !req?.error) {
                
                const raw_data = req.response.data;
                
                const obj = Factories.payment_methods_obj_factory(raw_data)
                
                CommonClass.payment_methods_obj = obj
                
                return obj
                
            } else {
                
                return {}
                
            }
            
        } else {
            
            return CommonClass.payment_methods_obj
        }
    }
    
    const applyCrLineTransaction = async (data:any) => {
        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }
        
        const req = await loansReqs().cr_line_transaction(data, r_token);
        
        return req.status
    }
    
    const pre_apply_loan = async (form:any, creditType:CreditTypes, user?: UserInterface, prod_id?:any) => {
        let r_token:string | undefined  = undefined
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        let amn =  form.get("amount")
        let period = form.get("period")
        let phone =  user?.phone.length ? CommonClass.formatNumber(user?.phone.replace(country_code, "")) : form.get("phone")

        let data:LoanBodyInterface = {
            credit_amount: amn,
            number_of_installments: period,
            idnx: user?.idnx || "",
            goods_name: creditType,
            birth_date: user?.birthday || "",
            first_name: user?.first_name || "",
            last_name: user?.last_name || "",
            product_id: String(prod_id) || String(form.product_id),
            mobile: country_code + phone?.replaceAll(" ", ""),
        }

        const req = await loansReqs().pre_apply(data as LoanBodyInterface, r_token);
        return req.status
    }

    const uncommited_loan = async (form:any, user?: UserInterface) => {
        let r_token:string | undefined  = undefined

        if(CommonClass.general_settings.recaptcha.is_enabled) {
            r_token = await getRecaptchaToken()
        }

        let amn =  form.get("amount")
        let period = form.get("period")
        let phone =  user?.phone.length ? CommonClass.formatNumber(user?.phone.replace(country_code, "")) : form.get("phone")

        let data:UncommitedInterface = {
            credit_amount: amn,
            number_of_installments: period,
            primary_phone: country_code + phone?.replaceAll(" ", ""),
        }

        const req = await loansReqs().uncommited(data, r_token);
        return req.status
    }
    
    return {
        updateUserLoans,
        clearLoanApplications,
        clearCredits,
        getLoansByType,
        getExpiredLoans,
        getLoanByContract,
        apply_for_loan,
        updPendingLoans,
        getContract,
        signContractSt1,
        signContractSt2,
        get_schedule_html,
        prefetch_contract,
        readContract,
        sendContract,
        getMethods,
        applyCrLineTransaction,
        getCrLineTransaction,
        getTransactionContract,
        readTransactionContract,
        signTransactionContractSt1,
        signTransactionContractSt2,
        prefetch_transaction_contract,
        pre_apply_loan,
        uncommited_loan,
        get_last_cr_line,
        pend_status_loans_length,
        getStandardCredits
    }
}

export default useLoans